import Enumerable from "linq";
import {
  Document,
  Section,
  ItemSet,
} from "../../../../domain/document";
import {
  IItem,
  AbstractCheckableItem,
  instanceOfIRelatedExternalScriptExecutable,
  instanceOfIPasteRequestable,
  instanceOfIDateLinkRequestable,
} from "../../../../domain/document/items";
import {
  ExternalScriptRunner,
  ObjectPositionSlider,
} from ".";
import { DateLinkExecutor, IDocumentEditable, IDocumentItemUpdateOptions, PasteExecutor } from "..";
import { SourceApplicator } from "../SourceApplicator";

export class PreEditDocument implements IDocumentEditable {
  constructor(
    source: Document,
    // TODO: 見直し.本来はEditingDocumentに内包したくないが、現状癒着が激しいので許容する.
    externalScriptRunner?: ExternalScriptRunner
  ) {
    this._source = source;
    this._externalScriptRunner = externalScriptRunner
      ? externalScriptRunner
      : new ExternalScriptRunner(this);
    this._externalScriptRunner.source = this;
    this._externalScriptRunner.shouldRestrictScript = source.shouldRestrictExternalScript;
    this._externalScriptRunner.allowedScripts = source.shouldRestrictExternalScript
      ? source.allowedExternalScripts
      : null;
  }

  private _externalScriptRunner: ExternalScriptRunner;

  get externalScriptRunner(): ExternalScriptRunner {
    return this._externalScriptRunner;
  }

  private _source: Document;

  get source(): Document {
    return this._source;
  }

  set source(source: Document) {
    this._source = source;
  }

  get itemSets(): ItemSet[] {
    return this.source
      ? this.source.itemSets
      : [];
  }

  get sections(): Section[] {
    return this.source
      ? this.source.sections
      : [];
  }

  insertItemSet(itemSet: ItemSet, sections: Section[], sectionIndex: number): number {
    if (sectionIndex < 0) { throw new Error("sectionIndex is invalid!!!"); }
    
    // DocumentのItemSetsに追加
    const newItemSetId = this.itemSets.length;
    this.itemSets.splice(newItemSetId , 0, itemSet.clone());

    // Sectionにキー情報を格納(追加の準備をする)
    const newSections: Section[] = [];
    for (const section of sections) {
      const cloned = section.clone();
      cloned.itemSetId = newItemSetId;
      newSections.push(cloned);
    }

    // DocumentのSectionsに追加
    for (let i = 0; i < newSections.length; i++) {
      const newSection = newSections[i];
      this.sections.splice(sectionIndex + i, 0, newSection);
    }

    return newItemSetId;
  }

  removeSection(section: Section): void {
    const itemSetId = section.itemSetId;
    
    // DocumentのSectionsから削除
    const sectionIndex = this.getSectionIndex(section);
    if (sectionIndex < 0) {
      throw new Error("section not found!!!");
    }
    this.sections.splice(sectionIndex, 1);

    // itemSetがどこからも使用されていなくなっていたら削除する
    if (Enumerable.from(this.sections).all(s => s.itemSetId !== itemSetId)) {
      this.itemSets.splice(itemSetId, 1);
    }
  }

  private getSectionIndex(section: Section): number {
    for (let index = 0; index < this.sections.length; ++index) {
      if (this.sections[index] === section) { return index; }
    }
    return -1;
  }

  addItem(added: IItem, sectionIndex?: number): void {
    const section = this.sections[sectionIndex];
    const items = this.itemSets[section.itemSetId].items;
    added.privateId = items.length;
    items.push(added);
    const cloned = section.clone();
    cloned.itemPrivateIds.push(added.privateId);
    this.sections.splice(sectionIndex, 1, cloned);
  }

  removeItem(removed: IItem, sectionIndex?: number): void {
    const section = this.sections[sectionIndex];
    const items = this.itemSets[section.itemSetId].items;
    items.splice(removed.privateId, 1, null);  // 削除するとprivateIdとindexがずれてしまうのでnullと置き換える.
    const cloned = section.clone();
    cloned.itemPrivateIds.splice(cloned.itemPrivateIds.indexOf(removed.privateId), 1);
    this.sections.splice(sectionIndex, 1, cloned);
  }

  updateItem(updated: IItem, options?: IDocumentItemUpdateOptions): void {
    const itemSetId = options ? options.itemSetId : undefined;
    const items = this.itemSets[itemSetId].items;
    const oldItem = items[updated.privateId];
    items.splice(updated.privateId, 1, Object.freeze(updated));

    const isGeometryOnly = !!options && !!options.isGeometryOnly;
    if (isGeometryOnly) {
      return; // 座標系のみの更新を明示的に指定されてる場合は連鎖更新しない.
    }

    if (instanceOfIRelatedExternalScriptExecutable(updated) && updated.isValueUpdated(oldItem)) {
      this.externalScriptRunner.addScriptExecuteRequest(itemSetId, updated);
    }
    // 文書内転記は後勝ちで更新されるので反映される順番によって結果が変わりうる.連鎖更新初期化前にも連鎖更新させる.
    if (instanceOfIPasteRequestable(updated) && updated.isValueUpdated(oldItem)) {
      const pasteExecutor = new PasteExecutor(this, false);
      pasteExecutor.execute(updated, itemSetId);
    }
    if (instanceOfIDateLinkRequestable(updated) && updated.isValueUpdated(oldItem)) {
      const dateLinkExecutor = new DateLinkExecutor(this);
      dateLinkExecutor.execute(updated, itemSetId);
    }

    // 高さ変更による再配置が必要な場合は再配置を行う.
    const slider = new ObjectPositionSlider(this);
    slider.apply(itemSetId, oldItem, updated);

    // チェック連鎖(単一選択時の選択解除等)も更新されたオブジェクトと順番がないと初期化できない.連鎖更新初期化前にも連鎖更新させる.
    if (updated instanceof AbstractCheckableItem) {
      this.updateLinkedCheck(updated, itemSetId);
    }

    const sourceApplicator = new SourceApplicator(this);
    sourceApplicator.applicate(oldItem, updated, updated, itemSetId);
  }

  updateLinkedCheck(updated: AbstractCheckableItem, itemSetId: number): void {
    if (updated.isMultiCheckable || !updated.isChecked) { return; }
    if (!updated.groupKey || updated.groupKey === "") { return; }
    
    if (updated.shouldLinkAsWideScope) {
      this.updateLinkedCheckInSameItemSet(updated, itemSetId);
    } else {
      this.updateLinkedCheckInSameSection(updated, itemSetId);
    }
  }

  private updateLinkedCheckInSameItemSet(updated: AbstractCheckableItem, itemSetId: number): void {
    for (const section of this.sections) {
      if (section.itemSetId !== itemSetId) { continue; }
      for (const privateId of section.itemPrivateIds) {
        if (privateId === updated.privateId) { continue; }
        const item = this.itemSets[itemSetId].items[privateId];
        if (item instanceof AbstractCheckableItem && item.groupKey === updated.groupKey && item.typeKey === updated.typeKey && item.isChecked && item.shouldLinkAsWideScope) {
          const cloned = item.clone() as AbstractCheckableItem;
          cloned.isChecked = false;
          this.updateItem(cloned, {
            itemSetId,
          });
        }
      }
    }
  }

  private updateLinkedCheckInSameSection(updated: AbstractCheckableItem, itemSetId: number): void {
    for (const section of this.sections) {
      if (section.itemSetId !== itemSetId) { continue; }
      if (section.itemPrivateIds.indexOf(updated.privateId) < 0) { continue; }
      // 同一セクションのオブジェクトのみを対象とする
      for (const privateId of section.itemPrivateIds) {
        if (privateId === updated.privateId) { continue; }
        const item = this.itemSets[itemSetId].items[privateId];
        if (item instanceof AbstractCheckableItem && item.groupKey === updated.groupKey && item.typeKey === updated.typeKey && item.isChecked && !item.shouldLinkAsWideScope) {
          const cloned = item.clone() as AbstractCheckableItem;
          cloned.isChecked = false;
          this.updateItem(cloned, {
            itemSetId,
          });
        }
      }
    }
  }

  updateItemToItemSet(updated: IItem, itemSetId: number): void {
    const items = this.itemSets[itemSetId].items;
    items.splice(updated.privateId, 1, updated);
  }

  clone(): PreEditDocument {
    return new PreEditDocument(
      this.source,
      this.externalScriptRunner,
    );
  }
}
